/*
  Größenrechner (Entwurf 28.10.2026, nicht live), gerendert von snippets/groessenrechner.liquid.
  Rechenregel wie die Größentabelle (groessentext.py), alles in mm:
  bis = Innenlänge minus Zugabe; von = bis der kleineren Größe + 1 (kleinste Größe: bis minus Abstand zur nächsten + 1).
  Empfehlung = Größe, in deren Spanne der Fuß liegt; unter 2 mm Luft bis „bis“ zusätzlich die nächste Größe.
*/

const SPEICHER = 'sd-fusslaenge';
const KNAPP_MM = 2;

export function spannen(daten, zugabe) {
  const g = [...daten].sort((a, b) => parseFloat(a.s) - parseFloat(b.s));
  const bis = g.map((x) => x.i - zugabe);
  return g.map((x, k) => ({
    ...x,
    bis: bis[k],
    von: k === 0 ? (g.length > 1 ? bis[0] - (bis[1] - bis[0]) + 1 : bis[0]) : bis[k - 1] + 1,
  }));
}

/* Eingabe in cm („24,3“, „24.3“) oder mm („243“) -> mm, sonst null */
export function lesen(text) {
  const t = String(text || '').trim().replace(',', '.').replace(/\s*(cm|mm)$/i, '');
  if (!/^\d{1,3}(\.\d)?$/.test(t)) return null;
  const z = parseFloat(t);
  const mm = z >= 80 ? Math.round(z) : Math.round(z * 10);
  return mm >= 80 && mm <= 350 ? mm : null;
}

export function empfehlen(reihe, fuss) {
  if (!reihe.length) return { art: 'leer' };
  if (fuss < reihe[0].von) return { art: 'zu-klein', kleinste: reihe[0] };
  const k = reihe.findIndex((x) => fuss >= x.von && fuss <= x.bis);
  if (k < 0) return { art: 'zu-gross', groesste: reihe[reihe.length - 1] };
  const knapp = reihe[k].bis - fuss < KNAPP_MM && k + 1 < reihe.length;
  return { art: 'treffer', groesse: reihe[k], auch: knapp ? reihe[k + 1] : null };
}

const cm = (mm) => (mm / 10).toFixed(1).replace('.', ',');
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

function gemerkt(gruppe) {
  try {
    return JSON.parse(localStorage.getItem(SPEICHER) || '{}')[gruppe] || null;
  } catch (e) {
    return null;
  }
}

function merken(gruppe, mm) {
  try {
    const d = JSON.parse(localStorage.getItem(SPEICHER) || '{}');
    if (mm === null) delete d[gruppe];
    else d[gruppe] = mm;
    localStorage.setItem(SPEICHER, JSON.stringify(d));
  } catch (e) {
    /* ohne Speicher geht der Rechner trotzdem */
  }
}

const Basis = typeof HTMLElement !== 'undefined' ? HTMLElement : class {};

class GroessenRechner extends Basis {
  connectedCallback() {
    if (this.reihe) return;
    try {
      this.reihe = spannen(JSON.parse(this.querySelector('[data-grr-daten]').textContent), Number(this.dataset.zugabe));
    } catch (e) {
      return;
    }
    this.modus = this.dataset.modus;
    this.gruppe = this.dataset.gruppe;
    this.feld = this.querySelector('.grr__fuss');
    this.ausgabe = this.querySelector('.grr__ergebnis');
    this.panel = this.querySelector('.grr__panel');

    const knopf = this.querySelector('.grr__oeffnen');
    if (knopf) {
      knopf.addEventListener('click', () => {
        const auf = this.panel.hidden;
        this.panel.hidden = !auf;
        knopf.setAttribute('aria-expanded', String(auf));
        if (auf) this.feld.focus();
      });
    }
    /* kein eigenes <form>: im Warenkorb steht der Rechner im Warenkorb-Formular, verschachtelte Formulare verwirft der Browser */
    this.querySelector('.grr__los').addEventListener('click', () => this.rechnen(true));
    /* beim Tippen rechnen, sobald ein gültiger Wert dasteht; leeres Feld klappt wieder zu */
    this.feld.addEventListener('input', () => {
      clearTimeout(this.tippen);
      /* Feld geleert: gemerkte Fußlänge vergessen, sonst füllt das Nachladen (unten) sie wieder ein */
      if (!this.feld.value.trim()) merken(this.gruppe, null);
      this.tippen = setTimeout(() => this.rechnen(lesen(this.feld.value) !== null), 500);
    });
    this.feld.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      this.rechnen(true);
    });
    this.ausgabe.addEventListener('click', (e) => {
      const b = e.target.closest('[data-grr-waehlen], [data-grr-entfernen]');
      if (!b) return;
      if (b.dataset.grrWaehlen) this.waehlen(b.dataset.grrWaehlen, b);
      if (b.dataset.grrEntfernen) this.entfernen(b.dataset.grrEntfernen, b);
    });

    this.tabelle(null);
    this.vorbelegen();
    /* Warenkorb und Variantenwechsel laden Teile der Seite nach und gleichen dabei auch dieses Element ab
       (Feld und Ergebnis wieder leer): dann mit der gemerkten Fußlänge neu füllen */
    new MutationObserver(() => {
      if (!this.feld.value && !this.ausgabe.innerHTML) this.vorbelegen();
    }).observe(this, { childList: true, subtree: true, attributes: true });
  }

  vorbelegen() {
    const alt = gemerkt(this.gruppe);
    if (!alt) return;
    this.feld.value = cm(alt);
    this.rechnen(false);
  }

  get zeilen() {
    try {
      return JSON.parse(this.querySelector('[data-grr-zeilen]').textContent);
    } catch (e) {
      return {};
    }
  }

  rechnen(neu) {
    const fuss = lesen(this.feld.value);
    /* Ergebnis und Tabelle erst nach einer gültigen Eingabe zeigen (Vorgabe Sven 28.10.) */
    const box = this.querySelector('.grr__tabelle-box');
    if (box) box.hidden = fuss === null;
    if (fuss === null) {
      this.ausgabe.innerHTML = neu ? '<p class="grr__fehler">Bitte die Fußlänge in cm eingeben, z. B. 24,5.</p>' : '';
      this.tabelle(null);
      return;
    }
    if (neu) merken(this.gruppe, fuss);
    const e = empfehlen(this.reihe, fuss);
    this.tabelle(e.art === 'treffer' ? e.groesse.v : null);
    this.ausgabe.innerHTML = this.modus === 'warenkorb' ? this.textWarenkorb(e, fuss) : this.textProdukt(e, fuss);
  }

  textProdukt(e, fuss) {
    const r = this.reihe;
    if (e.art === 'zu-klein')
      return `<p>Für ${cm(fuss)} cm ist dieses Modell zu groß: die kleinste Größe ${esc(r[0].s)} passt ab ${cm(r[0].von)} cm Fußlänge.</p>`;
    if (e.art === 'zu-gross')
      return `<p>Für ${cm(fuss)} cm ist dieses Modell zu klein: die größte Größe ${esc(e.groesste.s)} passt bis ${cm(e.groesste.bis)} cm Fußlänge.</p>`;
    const g = e.groesse;
    let h = `<p class="grr__treffer">Für ${cm(fuss)} cm Fußlänge passt Größe ${esc(g.s)}.</p>`;
    h += `<p>Innenlänge ${cm(g.i)} cm, passend für ${cm(g.von)} bis ${cm(g.bis)} cm Fußlänge (${this.dataset.zugabe} mm Spielraum eingerechnet).</p>`;
    if (!g.a) h += '<p>Diese Größe ist gerade nicht lieferbar.</p>' + this.andereFarben(g.s);
    if (e.auch) h += `<p>Dein Fuß liegt knapp an der Grenze. Wenn du mehr Platz magst${e.auch.a ? '' : ' (zurzeit nicht lieferbar)'}: Größe ${esc(e.auch.s)}.</p>`;
    let k = '';
    if (g.a) k += `<button type="button" class="grr__knopf" data-grr-waehlen="${esc(g.s)}">${esc(g.s)} in den Warenkorb</button>`;
    if (e.auch && e.auch.a) k += `<button type="button" class="grr__knopf grr__knopf--leise" data-grr-waehlen="${esc(e.auch.s)}">${esc(e.auch.s)} in den Warenkorb</button>`;
    return h + (k ? `<div class="grr__knoepfe">${k}</div>` : '');
  }

  /* Ausverkauft: andere Farben desselben Modells, in denen die Größe lieferbar ist (gleiche Tabelle je Modell) */
  andereFarben(groesse) {
    let farben = [];
    try {
      farben = JSON.parse(this.querySelector('[data-grr-farben]')?.textContent || '[]');
    } catch (e) {
      return '';
    }
    if (!farben.length) return '';
    const da = farben.filter((f) => f.g && f.g[groesse]);
    if (!da.length) return `<p>Auch in den anderen Farben ist Größe ${esc(groesse)} gerade ausverkauft.</p>`;
    const kacheln = da
      .map(
        (f) =>
          `<a class="grr__farbe" href="${esc(f.u)}?variant=${f.g[groesse]}">${f.b ? `<img src="${esc(f.b)}" alt="" width="56" height="56" loading="lazy">` : ''}<span>${esc(f.t)}</span></a>`
      )
      .join('');
    return `<p>In ${da.length > 1 ? 'diesen Farben' : 'dieser Farbe'} ist Größe ${esc(groesse)} lieferbar:</p><div class="grr__farben">${kacheln}</div>`;
  }

  textWarenkorb(e, fuss) {
    const zeilen = this.zeilen;
    const imKorb = this.reihe.filter((x) => zeilen[x.v]);
    if (e.art !== 'treffer') {
      const r = this.reihe;
      const grenze = e.art === 'zu-klein' ? `ab ${cm(r[0].von)} cm` : `bis ${cm(r[r.length - 1].bis)} cm`;
      return `<p>Für ${cm(fuss)} cm Fußlänge passt keine Größe dieses Modells (Größen ${esc(r[0].s)} bis ${esc(r[r.length - 1].s)}, ${grenze}).</p>`;
    }
    const passend = [e.groesse, e.auch].filter(Boolean);
    const ok = passend.filter((x) => zeilen[x.v]);
    const weg = imKorb.filter((x) => !passend.includes(x));
    let h = `<p class="grr__treffer">Für ${cm(fuss)} cm Fußlänge passt Größe ${esc(e.groesse.s)}${e.auch ? `, knapp auch ${esc(e.auch.s)}` : ''}.</p>`;
    if (!ok.length) {
      const ziel = `${this.dataset.url}?variant=${e.groesse.v}`;
      h += `<p>Diese Größe liegt nicht im Warenkorb. <a href="${esc(ziel)}">Größe ${esc(e.groesse.s)} ansehen</a></p>`;
    }
    if (weg.length) {
      h += `<p>Die ${weg.length > 1 ? 'anderen Größen brauchst du' : 'andere Größe brauchst du'} dann vermutlich nicht:</p>`;
      h += `<div class="grr__knoepfe">${weg
        .map((x) => `<button type="button" class="grr__knopf grr__knopf--leise" data-grr-entfernen="${esc(zeilen[x.v])}">${esc(x.s)} entfernen</button>`)
        .join('')}</div>`;
    }
    return h;
  }

  tabelle(trefferId) {
    const box = this.querySelector('.grr__tabelle');
    if (!box) return;
    const zeilen = this.reihe
      .map(
        (x) =>
          `<tr class="${x.v === trefferId ? 'ist-treffer' : ''}${x.a ? '' : ' ist-aus'}"><td>${esc(x.s)}</td><td>${cm(x.i)}</td><td>${cm(x.von)} – ${cm(x.bis)}</td></tr>`
      )
      .join('');
    box.innerHTML = `<table><thead><tr><th>Größe</th><th>Innenlänge (cm)</th><th>Fußlänge (cm)</th></tr></thead><tbody>${zeilen}</tbody></table>`;
  }

  /* Produktseite (Vorgabe Sven 28.10.): Größe in der Variantenauswahl setzen und in den Warenkorb legen.
     Erst das Größen-Feld anklicken (Preis, Bestand, Menü ziehen mit), dann den Warenkorb-Knopf des Themes,
     sobald das Formular die neue Variante trägt; so öffnet sich die Warenkorb-Schublade wie gewohnt. */
  async waehlen(groesse, knopf) {
    const ziel = this.reihe.find((x) => x.s === groesse);
    if (!ziel) return;
    const bereich = this.closest('.product-details, product-information, main') || document;
    const feld = [...bereich.querySelectorAll('variant-picker input[type="radio"]')].find(
      (i) => i.value === groesse && i.name.startsWith('Größe')
    );
    knopf.disabled = true;
    const text = knopf.textContent;
    knopf.textContent = 'Wird hinzugefügt …';
    if (feld && !feld.checked) feld.click();

    let warenkorb = null;
    for (let t = 0; t < 40; t++) {
      const form = bereich.querySelector('product-form-component form, form[data-type="add-to-cart-form"]');
      const id = form?.querySelector('input[name="id"]');
      const k = form?.querySelector('[ref="addToCartButton"], button[type="submit"]');
      if (id && String(id.value) === String(ziel.v) && k && !k.disabled) {
        warenkorb = k;
        break;
      }
      await new Promise((r) => setTimeout(r, 100));
    }

    let ok = false;
    if (warenkorb) {
      warenkorb.click();
      ok = true;
    } else {
      /* Rückfall: direkt über die Warenkorb-Schnittstelle, danach zum Warenkorb */
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: ziel.v, quantity: 1 }] }),
        });
        ok = r.ok;
        if (ok) window.location.href = '/cart';
      } catch (e) {
        ok = false;
      }
    }
    knopf.textContent = ok ? `${groesse} liegt im Warenkorb` : text;
    knopf.disabled = ok;
    if (!ok) this.ausgabe.insertAdjacentHTML('beforeend', '<p class="grr__fehler">Das hat leider nicht geklappt. Bitte wähle die Größe oben und lege sie über „In den Warenkorb legen“ ab.</p>');
  }

  /* Warenkorb: den vorhandenen Entfernen-Knopf der Zeile nutzen, damit Summen und Schublade wie gewohnt neu laden */
  entfernen(key, knopf) {
    const bereich = this.closest('.cart-items') || document;
    const zeile = bereich.querySelector(`tr[data-key="${CSS.escape(key)}"] .cart-items__remove`);
    if (zeile) {
      knopf.disabled = true;
      zeile.click();
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('groessen-rechner')) {
  customElements.define('groessen-rechner', GroessenRechner);
}
